import React, { useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  dataLength: number;
  goToPage: (pageNumber: number) => void;
  onPageSizeChange?: (newSize: number) => void;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export const TablePagination: React.FC<TablePaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  dataLength,
  goToPage,
  onPageSizeChange,
}) => {
  const pageWindow = useMemo(() => {
    const delta = 2;
    const pages: (number | 'ellipsis')[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    pages.push(1);
    if (left > 2) pages.push('ellipsis');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('ellipsis');
    pages.push(totalPages);

    return pages;
  }, [currentPage, totalPages]);

  const from = (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, dataLength);

  return (
    <div className="px-5 py-3.5 border-t border-gray-100 bg-white">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* Left: entry count + page size selector */}
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-xs text-gray-400 tabular-nums">
            <span className="font-semibold text-gray-600">
              {from}–{to}
            </span>{' '}
            of <span className="font-semibold text-gray-600">{dataLength}</span>{' '}
            entries
          </p>

          {onPageSizeChange && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400">Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  onPageSizeChange(Number(e.target.value));
                  goToPage(1); // reset to first page on size change
                }}
                className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 cursor-pointer hover:border-gray-300 transition-colors"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-400">rows</span>
            </div>
          )}
        </div>

        {/* Right: page controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            title="First page"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronsLeft size={15} />
          </button>

          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            title="Previous page"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft size={15} />
          </button>

          <div className="flex items-center gap-0.5 mx-1">
            {pageWindow.map((page, idx) =>
              page === 'ellipsis' ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="w-8 text-center text-xs text-gray-400 select-none"
                >
                  ···
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`min-w-[32px] h-8 px-1 rounded-md text-xs font-medium transition-all ${
                    currentPage === page
                      ? 'bg-blue-600 text-white shadow-sm shadow-blue-200 scale-105'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                  }`}
                >
                  {page}
                </button>
              ),
            )}
          </div>

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            title="Next page"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight size={15} />
          </button>

          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            title="Last page"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronsRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TablePagination;
